import { streaming } from 'core';
import { logout } from 'lib';
import io from 'socket.io-client';
import { store } from '../store';
import { getToken } from './';
// Socket.IO lives on the ROOT host (NOT /api). Derive it from the REST base so the
// socket always targets the SAME environment as the API (local → http://localhost:4002,
// cloud → https://p4pdev.niftycoders.net). REACT_APP_WS_URL is only a last-resort
// fallback: it had drifted to the cloud host while the REST base pointed at localhost,
// so realtime events emitted by the local backend never reached the client.
const REST_URL = process.env.REACT_APP_API_URL || '';
const SOCKET_URL = REST_URL.replace(/\/api\/?$/, '') || process.env.REACT_APP_WS_URL;

let socket, timer;
// Persistent listener registry so feature subscriptions (e.g. TEAM_CHANNEL_UPDATE)
// survive a disconnect()/connect() cycle (logout → login). On each connect we
// re-attach everything in here. socket.io handles its own auto-reconnects on the
// same instance, so those don't lose listeners either.
const subscriptions = [];

function send(msg, payload) {
  if (!socket) return;

  socket.send(msg, payload);
}

// A named event, unlike send() which always goes out as 'message'. Returns false while there is
// no connected socket, so a caller can say the event was not sent.
function emit(event, payload) {
  if (!socket?.connected) return false;

  socket.emit(event, payload);
  return true;
}

// Subscribe to a raw socket event (e.g. 'TEAM_CHANNEL_UPDATE', 'connect'). Safe to
// call before connect() resolves — the listener attaches as soon as the socket exists.
function on(event, cb) {
  subscriptions.push({ event, cb });
  if (socket) socket.on(event, cb);
}

function off(event, cb) {
  const i = subscriptions.findIndex((s) => s.event === event && s.cb === cb);
  if (i !== -1) subscriptions.splice(i, 1);
  if (socket) socket.off(event, cb);
}

async function connect() {
  if (socket) return console.log('TRIED TO CONNECT TO WS');

  const currentUserId = store.getState()?.user._id;

  const token = await getToken();
  if (!token) {
    store.dispatch({
      type: 'LOGOUT',
      payload: null
    });

    return;
  }

  // Pass the JWT three ways for compatibility with the server's socketio-jwt handshake:
  // socket.io `auth`, query string, and an Authorization header.
  socket = io(SOCKET_URL, {
    transports: ['websocket', 'polling'],
    auth: { token },
    query: { token },
    extraHeaders: { Authorization: `Bearer ${token}` }
  });

  // Re-attach feature listeners registered via on() before/after this connect.
  subscriptions.forEach(({ event, cb }) => socket.on(event, cb));

  socket.on('connect', () => {
    console.log('connected to ws');
    clearTimeout(timer);
    timer = setTimeout(() => {
      store.dispatch({
        type: 'CONNECTED',
        payload: true
      });
    }, 1000);
  });

  socket.onAny(async (event, args) => {
    if (event === 'FORCE_LOGOUT') return logout();
    // console.log('---------------------------------------------------------------------------------')
    // console.warn('Event', event, args)
    // console.log('---------------------------------------------------------------------------------')

    store.dispatch({
      type: event,
      payload: args
    });
  });

  socket.on('joinConnect', (data) => {
    if (data.senderId !== currentUserId) return;

    streaming.sendOffer(data);
  });

  socket.on('newOffer', (data) => {
    if (data.senderId === currentUserId) return;

    streaming.onOffer({
      ...data,
      userId: currentUserId
    });
  });

  socket.on('newAnswer', (data) => {
    if (data.senderId === currentUserId) return;
    streaming.onAnswer(data);
  });

  socket.on('iceCandidate', (data) => {
    if (data.senderId === currentUserId) return;
    streaming.addIceCandidate(data);
  });

  socket.on('videoMute', (data) => {
    const { clientStates } = store.getState().streaming;
    if (!clientStates || !clientStates[data.senderId]) return;

    clientStates[data.senderId].videoMute = data.disabled;

    store.dispatch({
      type: 'UPDATE_STREAMING',
      payload: { clientStates }
    });
  });

  /*
    Sends the current muted state to the other peer
    when an ice connection establishes
  */
  socket.on('sendMediaState', (data) => {
    const { clientStates } = store.getState().streaming;
    if (clientStates && clientStates[data.senderId]) return;

    clientStates[data.senderId] = data;

    store.dispatch({
      type: 'UPDATE_STREAMING',
      payload: { clientStates }
    });
  });

  socket.on('leaveConnect', (data) => {
    streaming.hangUp(data);
  });

  socket.on('liveStreamMessage', (data) => {
    store.dispatch({
      type: 'NEW_LIVE_STREAM_MESSAGE',
      payload: data
    });
  });

  socket.on('disconnect', (reason) => {
    console.log('disconnected from ws');
    clearTimeout(timer);

    store.dispatch({
      type: 'CONNECTED',
      payload: false
    });
  });

  socket.on('shoutoutRequest', (data) => {
    console.log('Shoutout request notification', data);

    store.dispatch({
      type: 'SHOUTOUT_REQUEST',
      payload: { showtoast: true }
    });
  });
}

function disconnect() {
  socket.disconnect();
  socket = undefined;
}

/**
 * GoalRush socket event names, kept in sync with playaz4playaz-backend `constants/socketEvents.ts`
 * (payloads: specs/P4P-576-goalrush-engine/contracts/socket-events.md). Declared for one naming
 * across the three apps; no CMS screen listens to them yet.
 */
export const GOALRUSH_EVENTS = Object.freeze({
  INITIAL: 'GOALRUSH_INITIAL',
  COUNTERS: 'GOALRUSH_COUNTERS',
  SHOTS: 'GOALRUSH_SHOTS',
  PRESENCE: 'GOALRUSH_PRESENCE',
  SPOT: 'GOALRUSH_SPOT',
  KICK_ACK: 'GOALRUSH_KICK_ACK',
  KICK_REJECTED: 'GOALRUSH_KICK_REJECTED',
  MILESTONE: 'GOALRUSH_MILESTONE',
  SESSION_RESET: 'GOALRUSH_SESSION_RESET',
  JOIN: 'join_goalrush',
  LEAVE: 'leave_goalrush',
  KICK: 'goalrush_kick'
});

export default {
  connect,
  disconnect,
  send,
  emit,
  on,
  off
};
